import React, {useEffect, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, Grid, IconButton, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HotelsTable from "../../Table/Hotels/HotelsTable";
import CustomButtonWithIcon from "../../../../components/CustomButtonWithIcon/Buttons";

const HotelsAccessAccordion = ({ expanded, handleChange, hotelsData, preselectedHotels, onSaveHotels, onCancel, accountName }) => {
    const [rowSelection, setRowSelection] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;
    const [sorting, setSorting] = useState([]);

    useEffect(() => {
        const preselectRows = {};
        preselectedHotels.forEach(hotelId => {
            preselectRows[hotelId] = true;
        });
        setRowSelection(preselectRows);
        setCurrentPage(1);
    }, [preselectedHotels]);

    const handleRowSelectionChange = (newRowSelection) => {
        setRowSelection(newRowSelection);
    };

    const handleSelectAll = (selectAll) => {
        const allSelected = {};
        if (selectAll) {
            hotelsData.forEach(hotel => {
                allSelected[hotel.id] = true;
            });
        }
        setRowSelection(allSelected);
    };

    const handleSave = () => {
        setLoading(true);
        const selectedHotelIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
        onSaveHotels(selectedHotelIds).finally(() => setLoading(false));
    };

    const sortedData = React.useMemo(() => {
        if (sorting.length === 0) return hotelsData;

        const sortColumn = sorting[0].id;
        const isDesc = sorting[0].desc;

        return [...hotelsData].sort((a, b) => {
            const aVal = a[sortColumn];
            const bVal = b[sortColumn];

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return isDesc
                    ? (bVal || '').localeCompare(aVal || '')
                    : (aVal || '').localeCompare(bVal || '');
            }

            if (aVal < bVal) return isDesc ? 1 : -1;
            if (aVal > bVal) return isDesc ? -1 : 1;
            return 0;
        });
    }, [hotelsData, sorting]);


    const paginatedData = sortedData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const handleNextPage = () => {
        if (currentPage * rowsPerPage < hotelsData.length) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prevPage) => prevPage - 1);
        }
    };

    return (
        <Accordion
            expanded={expanded === 'panel3'}
            onChange={handleChange('panel3')}
            disableGutters
            sx={{
                border: '1px solid #61D5A9', borderRadius: '8px!important', marginBottom: '10px',
                '&::before': expanded === 'panel3' ? {} : {content: 'none'},
                boxShadow: 'none',
                ...(expanded === 'panel3' && {
                    borderBottomLeftRadius: '2px!important',
                    borderBottomRightRadius: '2px!important',
                })
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{
                borderBottom: expanded === 'panel3' ? '1px solid #61D5A9' : 'none',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                height: '42px',
                minHeight: '42px',
                '&.Mui-expanded': {
                    minHeight: '42px',
                    height: '42px',
                },
                '.MuiAccordionSummary-content': {
                    margin: 0,
                }
            }}>
                <Grid container alignItems="center" sx={{ padding: '4px' }}>
                    <Typography sx={{fontSize:'12px',fontWeight: '400', fontFamily: 'Inter',
                        lineHeight: '14.52px',textTransform:'uppercase',color:"#373F41"}}>
                        {accountName} : Hotels Access
                    </Typography>
                </Grid>
            </AccordionSummary>
            <AccordionDetails sx={{
                backgroundColor: '#F5F5F5',
                height: '100%',
            }}>
                <Grid container spacing={2} sx={{ padding: '8px', justifyContent: 'center', alignItems: 'center' }}>
                    <Grid item xs={12}>
                        <HotelsTable
                            hotelsData={paginatedData}
                            rowSelection={rowSelection}
                            onRowSelectionChange={handleRowSelectionChange}
                            loading={loading}
                            onSelectAll={handleSelectAll}
                            sorting={sorting}
                            setSorting={setSorting}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <CustomButtonWithIcon
                            color="#b0acac"
                            hoverColor="#12794F"
                            titleColor="#373F41"
                            hoverTitleColor={"white"}
                            width={"120px"}
                            height={"24px"}
                            borderRadius="20px"
                            textStyle={{
                                fontFamily: 'Inter',
                                fontSize: '12px',
                                fontWeight: 500,
                                lineHeight: '14.52px',
                                textAlign: 'center',
                            }}
                            onClick={handleSave}
                            disabled={loading}
                        >
                            Save
                        </CustomButtonWithIcon>

                        <CustomButtonWithIcon
                            color="#b0acac"
                            hoverColor="#12794F"
                            titleColor="#373F41"
                            hoverTitleColor={"white"}
                            width={"120px"}
                            height={"24px"}
                            borderRadius="20px"
                            textStyle={{
                                fontFamily: 'Inter',
                                fontSize: '12px',
                                fontWeight: 500,
                                lineHeight: '14.52px',
                                textAlign: 'center',
                            }}
                            onClick={onCancel}
                        >
                            Cancel
                        </CustomButtonWithIcon>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={handlePreviousPage} disabled={currentPage === 1}>
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ fontSize: '14px', mx: 1 }}>
                            Page {currentPage} of {Math.ceil(hotelsData.length / rowsPerPage)}
                        </Typography>
                        <IconButton
                            onClick={handleNextPage}
                            disabled={currentPage * rowsPerPage >= hotelsData.length}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default HotelsAccessAccordion;
